import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, HelpCircle, Mail, Send, X, Book, Shield, Heart } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";

// Terms of Service Modal
export const TOSModal = ({ onClose }) => {
  const [content, setContent] = useState(null);
  const [activeSection, setActiveSection] = useState("tos"); // tos, rules

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const endpoint = activeSection === "tos" ? "/pages/tos" : "/pages/rules";
        const response = await axios.get(`${API}${endpoint}`);
        setContent(response.data);
      } catch (error) {
        console.error("Failed to fetch content:", error);
      }
    };
    fetchContent();
  }, [activeSection]);

  return (
    <DraggableResizableWindow
      title={<><FileText className="w-4 h-4" /> Terms of Service & Rules</>}
      onClose={onClose}
      initialSize={{ width: 600, height: 550 }}
      minSize={{ width: 400, height: 300 }}
      zIndex={7000}
      titleBarColor="bg-gradient-to-r from-blue-700 to-purple-700"
    >
      <div className="h-full flex flex-col">
        {/* Section Tabs */}
        <div className="flex bg-gray-200 p-1">
          <button
            className={`flex-1 py-2 px-3 text-xs font-bold rounded flex items-center justify-center gap-1 ${
              activeSection === "tos" ? "bg-blue-600 text-white" : "hover:bg-gray-300"
            }`}
            onClick={() => setActiveSection("tos")}
          >
            <FileText className="w-4 h-4" /> Terms of Service
          </button>
          <button
            className={`flex-1 py-2 px-3 text-xs font-bold rounded flex items-center justify-center gap-1 ${
              activeSection === "rules" ? "bg-purple-600 text-white" : "hover:bg-gray-300"
            }`}
            onClick={() => setActiveSection("rules")}
          >
            <Shield className="w-4 h-4" /> Community Rules
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto retro-scrollbar p-4 bg-white">
          {content ? (
            <div>
              <h2 className="text-lg font-bold text-center text-purple-700 mb-4">
                {content.title}
              </h2>
              <div className="text-sm whitespace-pre-wrap font-mono text-gray-800 leading-relaxed">
                {content.content}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}
        </div>
      </div>
    </DraggableResizableWindow>
  );
};

// Help Topics Modal
export const HelpModal = ({ onClose }) => {
  const [topics, setTopics] = useState([]);
  const [expandedTopic, setExpandedTopic] = useState(null);

  useEffect(() => {
    const fetchHelp = async () => {
      try {
        const response = await axios.get(`${API}/pages/help`);
        setTopics(response.data.topics || []);
      } catch (error) {
        console.error("Failed to fetch help:", error);
      }
    };
    fetchHelp();
  }, []);

  return (
    <DraggableResizableWindow
      title={<><HelpCircle className="w-4 h-4" /> Help Topics</>}
      onClose={onClose}
      initialSize={{ width: 550, height: 500 }}
      minSize={{ width: 380, height: 300 }}
      zIndex={7000}
      titleBarColor="bg-gradient-to-r from-green-600 to-teal-600"
    >
      <div className="p-4 h-full overflow-auto retro-scrollbar bg-gradient-to-b from-green-50 to-teal-50">
        <h2 className="text-lg font-bold text-center text-green-800 mb-4 flex items-center justify-center gap-2">
          <Book className="w-5 h-5" /> MEGA! Help Center
        </h2>

        <div className="space-y-2">
          {topics.map((topic, index) => (
            <div key={index} className="retro-window">
              <button
                className="w-full p-3 text-left flex items-center justify-between hover:bg-green-100"
                onClick={() => setExpandedTopic(expandedTopic === index ? null : index)}
              >
                <span className="font-bold text-sm text-green-700">{topic.title}</span>
                <span className="text-gray-500">{expandedTopic === index ? "▼" : "▶"}</span>
              </button>
              {expandedTopic === index && (
                <div className="p-3 bg-white border-t text-sm text-gray-700">
                  {topic.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {topics.length === 0 && (
          <div className="text-center py-8 text-gray-500">Loading help topics...</div>
        )}
      </div>
    </DraggableResizableWindow>
  );
};

// Contact Us Modal
export const ContactModal = ({ onClose, token }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error("Please fill out all fields");
      return;
    }

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim()
      }, { headers });
      
      toast.success("Message sent! We'll get back to you soon.");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableResizableWindow
      title={<><Mail className="w-4 h-4" /> Contact Us</>}
      onClose={onClose}
      initialSize={{ width: 450, height: 480 }}
      minSize={{ width: 350, height: 400 }}
      zIndex={7000}
      titleBarColor="bg-gradient-to-r from-orange-600 to-red-600"
    >
      <div className="p-4 h-full overflow-auto retro-scrollbar bg-gradient-to-b from-orange-50 to-red-50">
        <h2 className="text-lg font-bold text-center text-orange-800 mb-4 flex items-center justify-center gap-2">
          <Heart className="w-5 h-5 text-red-500" /> Get in Touch
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Have a question, issue, or suggestion? We'd love to hear from you!
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-bold mb-1">Your Name:</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="retro-inset w-full px-3 py-2 text-sm"
              placeholder="Enter your name"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Email Address:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="retro-inset w-full px-3 py-2 text-sm"
              placeholder="your@email.com"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Subject:</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="retro-inset w-full px-3 py-2 text-sm"
            >
              <option value="">Select a subject...</option>
              <option value="Technical Issue">Technical Issue</option>
              <option value="Account Problem">Account Problem</option>
              <option value="Report User">Report User</option>
              <option value="Billing Question">Billing Question</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Partnership Inquiry">Partnership Inquiry</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="retro-inset w-full px-3 py-2 text-sm h-24 resize-none"
              placeholder="Describe your issue or question..."
              maxLength={2000}
            />
            <p className="text-[10px] text-gray-500 text-right">{message.length}/2000</p>
          </div>

          <button
            type="submit"
            className="retro-button w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm flex items-center justify-center gap-2"
            disabled={submitting}
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </DraggableResizableWindow>
  );
};

export default { TOSModal, HelpModal, ContactModal };
